import { Directive, ElementRef, HostListener, Input, Renderer2, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';

/** Espace fine insécable : séparateur des milliers, le montant ne se coupe jamais. */
const SEPARATEUR = ' ';

/**
 * Champ de montant en FCFA : affiche « 20 000 » et transmet le nombre 20000 au modèle.
 * Les valeurs posées par le code (total calculé, monnaie à rendre) sont formatées elles aussi.
 * À utiliser sur un `<input type="text">` avec ngModel ; `montantMax` remplace l'attribut `max`
 * (réservé aux champs `type="number"`) et produit la même erreur `max`.
 */
@Directive({
  selector: 'input[appMontant]',
  standalone: true,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MontantInputDirective), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => MontantInputDirective), multi: true },
  ],
  host: { inputmode: 'numeric', autocomplete: 'off' },
})
export class MontantInputDirective implements ControlValueAccessor, Validator {
  @Input() set montantMax(valeur: number | null | undefined) {
    this.max = valeur ?? null;
    this.revalider();
  }

  private max: number | null = null;
  private onChange: (valeur: number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private revalider: () => void = () => {};

  constructor(private el: ElementRef<HTMLInputElement>, private renderer: Renderer2) {}

  writeValue(valeur: any): void {
    this.afficher(this.versNombre(valeur));
  }

  registerOnChange(fn: (valeur: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.revalider = fn;
  }

  setDisabledState(desactive: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', desactive);
  }

  validate(controle: AbstractControl): ValidationErrors | null {
    const valeur = controle.value;
    if (this.max === null || valeur === null || valeur === undefined || valeur === '') return null;
    return Number(valeur) > this.max ? { max: { max: this.max, actual: Number(valeur) } } : null;
  }

  @HostListener('input')
  saisie(): void {
    const champ = this.el.nativeElement;
    // Position du curseur exprimée en nombre de chiffres à sa gauche, pour la conserver après reformatage.
    const chiffresAvant = champ.value.slice(0, champ.selectionStart ?? champ.value.length).replace(/\D/g, '').length;
    const nombre = this.versNombre(champ.value);
    this.afficher(nombre);
    this.placerCurseur(chiffresAvant);
    this.onChange(nombre);
  }

  @HostListener('blur')
  sortie(): void {
    this.onTouched();
  }

  private versNombre(valeur: any): number | null {
    if (valeur === null || valeur === undefined || valeur === '') return null;
    if (typeof valeur === 'number') return isNaN(valeur) ? null : Math.trunc(valeur);
    const chiffres = String(valeur).replace(/\D/g, '');
    return chiffres ? parseInt(chiffres, 10) : null;
  }

  private afficher(nombre: number | null): void {
    const texte = nombre === null ? '' : String(nombre).replace(/\B(?=(\d{3})+(?!\d))/g, SEPARATEUR);
    this.renderer.setProperty(this.el.nativeElement, 'value', texte);
  }

  private placerCurseur(chiffresAvant: number): void {
    const champ = this.el.nativeElement;
    if (document.activeElement !== champ) return;
    let position = 0;
    let vus = 0;
    while (position < champ.value.length && vus < chiffresAvant) {
      if (/\d/.test(champ.value[position])) vus++;
      position++;
    }
    champ.setSelectionRange(position, position);
  }
}
